import { TagIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import type {
  FilterDefinition,
  FilterSuggestion,
  FilterGeneratorContext,
  SerializedFilter,
  ActiveFilter
} from '@oysterjs/ui/search-filter';
import type { KnowledgeBaseDocumentFieldsFragment } from '~/api/graphql';

export type KnowledgeBaseDocumentFilter = 'keyword' | 'title';

/**
 * Keyword filter - searches through all document keywords
 */
export class KeywordFilter implements FilterDefinition<KnowledgeBaseDocumentFilter> {
  readonly type = 'keyword';
  readonly icon = TagIcon;
  readonly priority = 10;
  readonly isUnique = false;

  private readonly allKeywords: string[];

  constructor(documents: KnowledgeBaseDocumentFieldsFragment[]) {
    // Collect all unique keywords from documents
    const keywordSet = new Set<string>();
    documents.forEach((doc) => {
      doc.metadata?.keywords?.forEach((k) => keywordSet.add(k));
    });
    this.allKeywords = Array.from(keywordSet);
  }

  generate({ query }: FilterGeneratorContext<KnowledgeBaseDocumentFilter>): FilterSuggestion[] {
    if (query.length < 2) return [];

    // Filter by query and return matching keywords
    return this.allKeywords
      .filter((k) => k.toLowerCase().includes(query.toLowerCase()))
      .slice(0, 10) // Limit to 10 suggestions
      .map((keyword) => ({
        value: keyword,
        displayLabel: 'Keyword is',
        displayValue: keyword
      }));
  }

  deserialize(
    serialized: SerializedFilter<KnowledgeBaseDocumentFilter>
  ): ActiveFilter<KnowledgeBaseDocumentFilter> {
    return {
      id: `keyword-${serialized.value}-${Date.now()}`,
      type: this.type,
      value: serialized.value,
      displayLabel: 'Keyword is',
      displayValue: serialized.displayValue
    };
  }
}

/**
 * Title/content search filter - searches document titles and summaries
 */
export class TitleFilter implements FilterDefinition<KnowledgeBaseDocumentFilter> {
  readonly type = 'title';
  readonly icon = DocumentTextIcon;
  readonly priority = 20;
  readonly isUnique = false;

  generate({ query }: FilterGeneratorContext<KnowledgeBaseDocumentFilter>): FilterSuggestion[] {
    if (query.length < 2) return [];

    return [
      {
        value: query,
        displayLabel: 'Title contains',
        displayValue: `"${query}"`
      }
    ];
  }

  deserialize(
    serialized: SerializedFilter<KnowledgeBaseDocumentFilter>
  ): ActiveFilter<KnowledgeBaseDocumentFilter> {
    return {
      id: `title-${serialized.value}-${Date.now()}`,
      type: this.type,
      value: serialized.value,
      displayLabel: 'Title contains',
      displayValue: serialized.displayValue
    };
  }
}

/**
 * Helper to create filter definitions with current documents
 */
export const createFilterDefinitions = (
  documents: KnowledgeBaseDocumentFieldsFragment[]
): FilterDefinition<KnowledgeBaseDocumentFilter>[] => [
  new KeywordFilter(documents),
  new TitleFilter()
];

/**
 * Apply active filters to documents
 */
export const applyFilters = (
  documents: KnowledgeBaseDocumentFieldsFragment[],
  filters: ActiveFilter<KnowledgeBaseDocumentFilter>[]
): KnowledgeBaseDocumentFieldsFragment[] => {
  if (filters.length === 0) return documents;

  return documents.filter((doc) => {
    return filters.every((filter) => {
      switch (filter.type) {
        case 'keyword':
          // Document must have this keyword
          return doc.metadata?.keywords?.some(
            (k) => k.toLowerCase() === filter.value.toLowerCase()
          );

        case 'title': {
          // Document title or summary must contain the search term
          const searchTerm = filter.value.toLowerCase();
          return (
            doc.title.toLowerCase().includes(searchTerm) ||
            doc.displayName?.toLowerCase().includes(searchTerm) ||
            doc.metadata?.title?.toLowerCase().includes(searchTerm) ||
            doc.metadata?.summary?.toLowerCase().includes(searchTerm)
          );
        }

        default:
          return true;
      }
    });
  });
};
